import { derivedRowDraftFields, quoteRowInputDefaults } from './shiftDraftInputSignature';
import { normalizeEmailList } from '../quoteSafety';

// Drafts retain editor work separately from the saved client quote and request audit.
const equal = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const workerFields = new Set(['desiredRate', 'requiredRate', 'availableQuantity', 'quickAcceptEnabled', 'fixedRate', 'workerRatePolicySnapshot']);
const sameId = (a: any, b: any) => String(a.id) === String(b.id);
const persistedId = (value: any) => Number.isSafeInteger(Number(value)) && Number(value) > 0;
const requestRowId = (value: any) => Number.isSafeInteger(value?.clientRequestHistoryShiftId) &&
  value.clientRequestHistoryShiftId > 0 ? value.clientRequestHistoryShiftId : null;
// A generated row can acquire a database ID when a quote is approved. Shift
// numbers are allocated by the editor, but are not unique in the database, so
// require one unambiguous match on the unchanged row terms as well.
const sameRowOrigin = (a: any, b: any) =>
  ['shiftNumber', 'position', 'quantity', 'rateType', 'rate'].every(key =>
    a[key] !== undefined && b[key] !== undefined && String(a[key]) === String(b[key]));
const dateValue = (value: unknown) => {
  const parsed = new Date(String(value));
  return Number.isFinite(parsed.getTime()) ? parsed.toISOString().slice(0, 10) : value;
};
const equalField = (a: unknown, b: unknown, key: string) => {
  if (key === 'clientEmail' || key === 'ccEmails') {
    return equal(normalizeEmailList(a), normalizeEmailList(b));
  }
  if ((key === 'startDate' || key === 'endDate') && a && b) {
    return equal(dateValue(a), dateValue(b));
  }
  if (key === 'agreementCalculationKey' && typeof a === 'string' && typeof b === 'string') {
    try {
      const normalized = (value: string) => {
        const parts = JSON.parse(value);
        return Array.isArray(parts) && parts[0] === 'quote-custom-rate-v3'
          ? [parts[0], parts[1], dateValue(parts[2]), ...parts.slice(3)] : value;
      };
      return equal(normalized(a), normalized(b));
    } catch { /* An unknown cache key retains exact comparison. */ }
  }
  return equal(a, b);
};
/** Retire a temporary UI identity once the same row exists in the current
 * persisted revision. Keep genuine new rows temporary until publication. */
export function reconcileQuoteDraftRowIds(base: any, positions: any[]) {
  if (!Array.isArray(base?.positions)) throw new Error('Quote revision is unavailable. Reopen the quote before saving.');
  return positions.map(row => {
    if (persistedId(row.id) || base.positions.some((item: any) => sameId(item, row))) return row;
    const historyId = requestRowId(row);
    if (historyId) {
      const linked = base.positions.filter((item: any) =>
        persistedId(item.id) && requestRowId(item) === historyId);
      if (linked.length > 1) throw new Error('Requested quote row identity is ambiguous. Reopen the quote before saving.');
      if (linked.length === 1 && positions.some(item => sameId(item, linked[0]))) {
        throw new Error('Requested quote row is duplicated. Reopen the quote before saving.');
      }
      return linked.length === 1 ? { ...row, id: linked[0].id } : row;
    }
    const candidates = base.positions.filter((item: any) => persistedId(item.id) && sameRowOrigin(item, row));
    if (candidates.length === 1 && !positions.some(item => sameId(item, candidates[0]))) {
      return { ...row, id: candidates[0].id };
    }
    if (base.positions.some((item: any) => persistedId(item.id) &&
        String(item.shiftNumber) === String(row.shiftNumber))) {
      throw new Error('Quote row identity is ambiguous. Reopen the quote and review the saved revision before saving.');
    }
    return row;
  });
}

/** Existing rows retain their IDs even when references were omitted from the
 * request. Only new rows use the exact submitted-order response contract. */
export function reconcileQuoteSaveResponse(positions: any[], submitted: any[], saved: any[], links: any[] = []) {
  const savedById = new Map(saved.map(row => [String(row.id), row]));
  const submittedIds = new Set(submitted.filter(row => persistedId(row.id)).map(row => String(row.id)));
  const orderedResponse = submitted.length === saved.length &&
    submitted.every((row, index) => !persistedId(row.id) || String(row.id) === String(saved[index].id));
  const newRows = new Map<string, any>();
  submitted.forEach((row, index) => {
    if (persistedId(row.id)) return;
    const historyId = requestRowId(row);
    const link = historyId ? links.find(item => item.historyShiftId === historyId) : null;
    const candidate = historyId ? savedById.get(String(link?.shiftId)) : orderedResponse ? saved[index] : null;
    if (candidate && persistedId(candidate.id) && !submittedIds.has(String(candidate.id))) {
      newRows.set(String(row.id), candidate);
    }
  });
  const mappedIds = [...newRows.values()].map(row => String(row.id));
  if (newRows.size !== submitted.filter(row => !persistedId(row.id)).length ||
      new Set(mappedIds).size !== mappedIds.length) {
    throw new Error('The saved quote row identities could not be confirmed. Keep your edits open and reload the saved quote.');
  }
  return positions.map(position => {
    const savedRow = persistedId(position.id) ? savedById.get(String(position.id)) : newRows.get(String(position.id));
    return savedRow ? { ...position, id: savedRow.id,
      shiftNumber: savedRow.shiftNumber ?? position.shiftNumber,
      revision: savedRow.revision ?? position.revision } : position;
  });
}

export function restoreQuoteRevisionDraft(current: any, saved: any, { mergeEditorRow = false } = {}) {
  const { _base: base, ...draft } = saved;
  if (!base || !Array.isArray(base.positions)) throw new Error('Saved draft has no original revision. Keep your current edits open.');
  let conflicts = 0;
  const aliases = new Map<string, string>();
  const unresolved = new Set<string>();
  draft.positions.forEach((edited: any) => {
    if (persistedId(edited.id)) return;
    // A base row with a temporary ID may have been published since save.
    const before = base.positions.find((item: any) => sameId(item, edited));
    const historyId = requestRowId(edited);
    const candidates = (before && !persistedId(before.id) ? current.positions : base.positions)
      .filter((item: any) => persistedId(item.id) && (historyId
        ? requestRowId(item) === historyId : sameRowOrigin(item, before ?? edited)))
      .filter((item: any) => current.positions.some((now: any) => sameId(now, item)));
    if (candidates.length === 1 &&
        !draft.positions.some((item: any) => sameId(item, candidates[0])) &&
        ![...aliases.values()].includes(String(candidates[0].id))) {
      aliases.set(String(edited.id), String(candidates[0].id));
    } else if (before ? !current.positions.some((item: any) => sameId(item, edited)) :
        !historyId && current.positions.some((item: any) => persistedId(item.id) &&
          String(item.shiftNumber) === String(edited.shiftNumber))) {
      unresolved.add(String(edited.id));
      conflicts += 1;
    }
  });
  const merge = (now: any, before: any, edited: any, row = false) => {
    const result = { ...now };
    Object.keys(edited).forEach(key => {
      if (key === 'positions' || (mergeEditorRow && key === 'editorBuffers') || (row && (key === 'id' || workerFields.has(key)))) return;
      // This cached amount is recalculated from restored rows and fee inputs
      // by the editor. Request loading can change it without an Admin edit.
      if (!row && key === 'adminFeeValue') { result[key] = edited[key]; return; }
      if (equalField(edited[key], before?.[key], key)) return;
      if (row && key === 'agreementCalculationKey' && equalField(now?.[key], edited[key], key)) return;
      if (equalField(now?.[key], before?.[key], key) || equalField(now?.[key], edited[key], key)) result[key] = edited[key];
      else conflicts += 1;
    });
    return result;
  };
  const result = merge(current, base, draft);
  result.positions = current.positions.flatMap((row: any) => {
    const alias = [...aliases].find(([, id]) => id === String(row.id))?.[0];
    const before = base.positions.find((item: any) => sameId(item, row) || (alias && String(item.id) === alias));
    const edited = draft.positions.find((item: any) => sameId(item, row) || (alias && String(item.id) === alias));
    if (!before) {
      const historyId = requestRowId(row);
      if (historyId && String(row.id) === `history-${historyId}` &&
          !base.positions.some((item: any) => requestRowId(item) === historyId)) {
        const matches = draft.positions.filter((item: any) => requestRowId(item) === historyId);
        const currentMatches = current.positions.filter((item: any) => requestRowId(item) === historyId);
        if (matches.length === 1 && currentMatches.length === 1 && sameId(matches[0], row)) {
          return [merge(row, row, matches[0], true)];
        }
        if (matches.length) conflicts += 1;
      }
      return [row];
    }
    if (!edited) {
      if (draft.positions.some((item: any) => unresolved.has(String(item.id)) &&
          String(item.shiftNumber) === String(row.shiftNumber))) return [row];
      if (equal(row, before)) return [];
      conflicts += 1;
      return [row];
    }
    return [merge(row, before, edited, true)];
  });
  draft.positions.forEach((row: any) => {
    if (!aliases.has(String(row.id)) && !unresolved.has(String(row.id)) &&
        !base.positions.some((item: any) => sameId(item, row)) &&
        !result.positions.some((item: any) => String(item.id) === String(row.id))) result.positions.push(row);
  });
  if (mergeEditorRow && draft.editorBuffers) {
    const buffers = draft.editorBuffers;
    const original = draft.positions.find((row: any) => String(row.id) === String(buffers.editingRowId));
    const rowId = aliases.get(String(buffers.editingRowId)) ?? buffers.editingRowId;
    const latest = result.positions.find((row: any) => String(row.id) === String(rowId));
    const patch: Record<string, any> = {};
    if (original && latest && buffers.row) {
      patch.id = latest.id;
      const defaults = quoteRowInputDefaults(original,
        original.isNew || original.__isNew || original.isHistoryNew ? 'new' : 'saved');
      const sameInput = (value: any, reference: any, key: string) => equalField(value, reference, key) ||
        (typeof reference === 'number' && typeof value === 'string' && value.trim() !== '' && Number(value) === reference);
      Object.keys(buffers.row).forEach(key => {
        if (key === 'id' || workerFields.has(key) || derivedRowDraftFields.has(key) || sameInput(buffers.row[key], original[key], key) ||
            (Object.prototype.hasOwnProperty.call(defaults, key) && sameInput(buffers.row[key], defaults[key], key))) return;
        if (sameInput(latest[key], original[key], key) || sameInput(buffers.row[key], latest[key], key)) {
          patch[key] = buffers.row[key];
        } else conflicts += 1;
      });
    } else if (buffers.editingRowId != null && Object.keys(buffers.row ?? {}).length) conflicts += 1;
    result.editorBuffers = { ...buffers, editingRowId: latest ? latest.id : null, row: patch };
  }
  return { snapshot: result, conflicts };
}
